<template>
    <div>
        <Head title="Équipements" />

        <!-- Toolbar -->
        <div class="flex flex-col md:flex-row gap-3 mb-4 justify-between items-center">
            <search-input v-model="form.search" placeholder="Nom, code, n° de série..." class="w-full max-w-md" @reset="reset" />
            <div class="flex gap-2 items-center flex-wrap">
                <select-input v-model="form.building_id" class="w-44">
                    <option :value="null">Tous les bâtiments</option>
                    <option v-for="b in buildings" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select-input>
                <select-input v-model="form.status" class="w-40">
                    <option :value="null">Tous les statuts</option>
                    <option value="operationnel">Opérationnel</option>
                    <option value="en_maintenance">En maintenance</option>
                    <option value="en_panne">En panne</option>
                    <option value="hors_service">Hors service</option>
                </select-input>
                <button class="btn-indigo" @click="openCreate">+ Nouvel équipement</button>
            </div>
        </div>

        <!-- Table -->
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <table class="min-w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                    <th class="px-6 py-4">Nom</th>
                    <th class="px-6 py-4">Code</th>
                    <th class="px-6 py-4">Type / Marque</th>
                    <th class="px-6 py-4">Bâtiment</th>
                    <th class="px-6 py-4">Statut</th>
                    <th class="px-6 py-4">Prochaine maint.</th>
                    <th class="px-6 py-4">Garantie</th>
                    <th class="px-6 py-4">Actions</th>
                </tr>
                <tr v-for="e in equipment.data" :key="e.id" class="border-t hover:bg-gray-100">
                    <td class="px-6 py-3 font-medium">
                        {{ e.name }}
                        <span v-if="e.maintenance_due" class="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">Maintenance !</span>
                    </td>
                    <td class="px-6 py-3 text-gray-500">{{ e.code || '—' }}</td>
                    <td class="px-6 py-3">{{ [e.type, e.brand].filter(Boolean).join(' / ') || '—' }}</td>
                    <td class="px-6 py-3">{{ e.building || '—' }}</td>
                    <td class="px-6 py-3">
                        <span :class="statusClass(e.status)" class="px-2 py-0.5 rounded-full text-xs font-semibold">{{ e.status || '—' }}</span>
                    </td>
                    <td class="px-6 py-3 text-sm" :class="e.maintenance_due ? 'text-red-600 font-semibold' : ''">
                        {{ e.next_maintenance_date || '—' }}
                    </td>
                    <td class="px-6 py-3 text-sm" :class="e.warranty_expired ? 'text-red-500' : ''">
                        {{ e.warranty_expiry || '—' }}
                        <span v-if="e.warranty_expired" class="text-xs">(exp.)</span>
                    </td>
                    <td class="px-6 py-3 flex gap-2">
                        <button class="text-indigo-600 hover:underline text-sm" @click="openEdit(e)">Modifier</button>
                        <button class="text-red-500 hover:underline text-sm" @click="destroy(e.id)">Supprimer</button>
                    </td>
                </tr>
                <tr v-if="equipment.data.length === 0">
                    <td class="px-6 py-4 border-t" colspan="8">Aucun équipement trouvé.</td>
                </tr>
            </table>
        </div>
        <pagination class="mt-4" :links="equipment.links" />

        <!-- Modal -->
        <div v-if="modal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-screen overflow-y-auto">
                <h2 class="text-lg font-bold mb-4">{{ editing ? 'Modifier l\'équipement' : 'Nouvel équipement' }}</h2>
                <form @submit.prevent="submit">
                    <div class="grid grid-cols-2 gap-3">
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Nom *</label>
                            <input v-model="form2.name" type="text" class="form-input w-full" required maxlength="150" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Code</label>
                            <input v-model="form2.code" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Type</label>
                            <input v-model="form2.type" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Marque</label>
                            <input v-model="form2.brand" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Modèle</label>
                            <input v-model="form2.model" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">N° de série</label>
                            <input v-model="form2.serial_number" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Bâtiment</label>
                            <select v-model="form2.building_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="b in buildings" :key="b.id" :value="b.id">{{ b.name }}</option>
                            </select>
                        </div>
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Emplacement détaillé</label>
                            <input v-model="form2.location_detail" type="text" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Date d'achat</label>
                            <input v-model="form2.purchase_date" type="date" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Fin de garantie</label>
                            <input v-model="form2.warranty_expiry" type="date" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Dernière maintenance</label>
                            <input v-model="form2.last_maintenance_date" type="date" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Prochaine maintenance</label>
                            <input v-model="form2.next_maintenance_date" type="date" class="form-input w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Statut</label>
                            <select v-model="form2.status" class="form-input w-full">
                                <option value="operationnel">Opérationnel</option>
                                <option value="en_maintenance">En maintenance</option>
                                <option value="en_panne">En panne</option>
                                <option value="hors_service">Hors service</option>
                            </select>
                        </div>
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Notes</label>
                            <textarea v-model="form2.notes" class="form-input w-full" rows="3"></textarea>
                        </div>
                    </div>
                    <div class="flex justify-end gap-2 mt-4">
                        <button type="button" class="btn-white" @click="modal = false">Annuler</button>
                        <button type="submit" class="btn-indigo" :disabled="submitting">
                            {{ submitting ? 'Enregistrement...' : 'Enregistrer' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import SelectInput from '@/Shared/SelectInput'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import throttle from 'lodash/throttle'

export default {
    layout: Layout,
    components: { Link, Head, Pagination, SearchInput, SelectInput },
    props: {
        equipment: Object,
        filters: Object,
        buildings: Array,
    },
    remember: 'form',
    data() {
        return {
            form: {
                search: this.filters.search,
                status: this.filters.status ?? null,
                building_id: this.filters.building_id ?? null,
            },
            modal: false,
            editing: null,
            submitting: false,
            form2: this.emptyForm(),
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('equipment.index'), pickBy(this.form), { replace: true, preserveState: true })
            }, 150),
        },
    },
    methods: {
        emptyForm() {
            return { name: '', code: '', type: '', brand: '', model: '', serial_number: '', building_id: null, location_detail: '', purchase_date: '', warranty_expiry: '', last_maintenance_date: '', next_maintenance_date: '', status: 'operationnel', notes: '' }
        },
        statusClass(s) {
            return {
                'bg-green-100 text-green-700': s === 'operationnel',
                'bg-yellow-100 text-yellow-700': s === 'en_maintenance',
                'bg-red-100 text-red-700': s === 'en_panne' || s === 'hors_service',
                'bg-gray-100 text-gray-600': !s,
            }
        },
        openCreate() {
            this.editing = null
            this.form2 = this.emptyForm()
            this.modal = true
        },
        openEdit(e) {
            this.editing = e.id
            this.form2 = { ...e, building_id: e.building_id ?? null }
            this.modal = true
        },
        submit() {
            this.submitting = true
            if (this.editing) {
                this.$inertia.put(this.route('equipment.update', this.editing), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            } else {
                this.$inertia.post(this.route('equipment.store'), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            }
        },
        destroy(id) {
            if (confirm('Supprimer cet équipement ?')) {
                this.$inertia.delete(this.route('equipment.destroy', id))
            }
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
